import { v4 } from 'uuid';

import { parseLegId } from '@/api/option/utils.ts';
import { associateBy, objectKeys } from '@/utils/object.ts';

import type { FxOption, LegId, OptionLegProperties, OptionProductName, OptionProperties, OptionUiState } from './state';

export function emptyOption(
  tileId: string,
  product: OptionProductName = 'Vanilla',
  props?: Partial<OptionProperties>,
  clientId?: number,
): FxOption {
  return {
    type: product ?? 'Vanilla',
    clientId,
    properties: {
      currencyPair: '',
      hedgeType: 'Live',
      rfs: { status: 'CANCELLED' },
      legs: [
        {
          legId: `${tileId}/0`,
          legProductName: getLegProductName(product),
        },
      ],
      premiumType: {
        premiumCategory: 'AMOUNT',
        premiumCurrency: 1,
      },
      rfsVersion: 0,
      hedges: [],
      uiState: defaultOptionUiState,
      mifid: { records: [] },
      ...props,
      tileId,
    },
    tradeCapture: {
      status: 'INITIALIZED',
      sessionId: v4(),
      version: -1,
    },
  };
}

export const defaultOptionUiState: OptionUiState = {
  strategyPanelMode: 'hidden',
  legsNotionalCurrenciesLinkState: 'linked',
  groupLegsByExpiry: false,
  expandGreekAndMarketPanels: false,
};

/**
 * Fully merges legs by their ids, using properties from both existingLegs and newLegs.
 * New legs will overwrite existing ones if they share the same legId.
 *
 * Only the legIds of newLegs will be kept, which allows removing legs.
 *
 * @param existingLegs existing legs
 * @param newLegs new legs to merge
 */
export function mergeLegs<T extends { legId: string }>(existingLegs: T[], newLegs: T[]): T[] {
  const legs1ById = associateBy(existingLegs, (leg) => leg.legId);
  const legs2ById = associateBy(newLegs, (leg) => leg.legId);

  const keys = objectKeys(legs2ById);
  const sortedKeys = keys.toSorted((leftValue, rightValue) => leftValue.localeCompare(rightValue, 'en'));

  return sortedKeys.map((legId) => {
    const leg1 = legs1ById[legId];
    const leg2 = legs2ById[legId];

    return {
      ...leg1,
      ...leg2,
      legId,
    } as T;
  });
}

/**
 * This merges legs by their ids, but only uses the properties of newLegs.
 * This is useful because to call TradeCapture, we want to send the ids of all the legs,
 * but only a limited number of new properties.
 *
 * @param existingLegs Legs whose ids will be used
 * @param newLegs legs whose and ids properties will be used
 */
export function shallowMergeLegs<T extends { legId: string }>(existingLegs: T[], newLegs: T[]): T[] {
  const legs1ById = associateBy(existingLegs, (leg) => leg.legId);
  const legs2ById = associateBy(newLegs, (leg) => leg.legId);

  const keys = new Set([...objectKeys(legs1ById), ...objectKeys(legs2ById)]);
  const sortedKeys = [...keys].sort((leftValue, rightValue) => leftValue.localeCompare(rightValue, 'en'));

  return sortedKeys.map((legId) => {
    const leg2 = legs2ById[legId];

    return {
      ...leg2,
      legId,
    } as T;
  });
}

/**
 * Build the id of a new leg based on the existing legs.
 * One cannot just create a legId based on the length of the legs array.
 * ex :
 *    if existing leg ids are '{tileId}/0' and '{tileId}/2' (for instance after have removed leg '{tileId}/1')
 *    then using the pattern `${tileId}/${legs.length}` would use '{tileId}/2' as new leg id
 *    this would result in no leg creation.
 * @param tileId
 * @param legs all existing legs
 * @returns the legId to create
 */
export function getNewLegId(tileId: string, legs: OptionLegProperties[]): LegId {
  const maxLegNumber = legs.reduce((acc, leg) => {
    const { legNumber } = parseLegId(leg.legId);
    return Number.isNaN(legNumber) ? acc : Math.max(acc, legNumber);
  }, 0);

  return `${tileId}/${maxLegNumber + 1}`;
}

const getLegProductName = (product: OptionProductName): 'Vanilla' | 'Trigger' | 'Binary' => {
  switch (product) {
    case 'Trigger':
      return 'Trigger';
    case 'Binary':
      return 'Binary';
    default:
      return 'Vanilla';
  }
};
